import { useEffect, useRef } from 'react';

// Everything an overlay owes a keyboard user, in one place.
//
// The photo lightbox got this right and the booking overlay did not: four tabs
// out of the booking form and focus was behind the backdrop, in page content
// that could not be seen and, with body overflow locked, could not be
// scrolled. aria-modal="true" changes what a screen reader exposes and traps
// nothing. Escape then dropped focus on the header logo rather than the button
// that opened the dialog.
//
// The lightbox's own trap only queried `button`, which was enough there and
// would have skipped every input in the booking form, so the selector below is
// the general one.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function focusableIn(root) {
  if (!root) return [];
  // offsetParent filters out anything display:none; a fixed-position element
  // reports null for it, hence the second test.
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed'
  );
}

/**
 * Traps Tab inside `panelRef` while `open`, locks the page behind it, moves
 * focus in on open, and returns it to whatever had focus before on close.
 *
 * @param {boolean} open
 * @param {{current: HTMLElement|null}} panelRef  the dialog element
 * @param {object} [options]
 * @param {() => void} [options.onEscape]         called on Escape
 * @param {{current: HTMLElement|null}} [options.initialFocusRef]
 *        what to focus on open; defaults to the first focusable element
 */
export function useModalFocus(open, panelRef, { onEscape, initialFocusRef } = {}) {
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    // Captured on open, restored on close: the button that opened the dialog.
    restoreRef.current = document.activeElement;

    function onKey(e) {
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(panelRef.current);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      // Focus outside the panel (a click on the backdrop, a browser that put
      // it on the document) comes back in rather than walking the page.
      if (!panelRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // The panel mounts in the same commit, so this runs with it in the DOM.
    const target = initialFocusRef?.current || focusableIn(panelRef.current)[0];
    target?.focus();

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Only if focus is still somewhere in the dialog being torn down.
      // Otherwise the guest has already clicked into the page and yanking
      // them back would be the rudeness this is meant to prevent.
      const active = document.activeElement;
      if (!active || active === document.body || panelRef.current?.contains(active)) {
        restoreRef.current?.focus?.();
      }
    };
  }, [open, panelRef, onEscape, initialFocusRef]);
}
